import React from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import {
  RegistrationMemberType,
  ServiceAgreementTemplateDto,
} from "../../../types/types";
import "./ServiceAgreementTemplatesEditor.css";

interface Props {
  value: ServiceAgreementTemplateDto[];
  onChange: (value: ServiceAgreementTemplateDto[]) => void;
}

const MEMBER_TYPES: Array<{
  value: RegistrationMemberType;
  label: string;
}> = [
  { value: "ServiceUser", label: "Service users" },
  { value: "PrimaryContact", label: "Primary contacts" },
  { value: "Volunteer", label: "Volunteers" },
  { value: "Staff", label: "Staff" },
];

export default function ServiceAgreementTemplatesEditor({
  value,
  onChange,
}: Props) {
  const [collapsedItems, setCollapsedItems] = React.useState<Set<number>>(
    () => new Set(value.map((_, index) => index)),
  );

  const update = (
    index: number,
    changes: Partial<ServiceAgreementTemplateDto>,
  ) => {
    onChange(
      value.map((template, templateIndex) =>
        templateIndex === index ? { ...template, ...changes } : template,
      ),
    );
  };

  const addTemplate = () => {
    onChange([
      ...value,
      {
        serviceAgreementTemplateId: 0,
        serviceId: 0,
        name: "",
        description: "",
        text: "",
        type: "Agreement",
        helpText: "",
        displayOrder: (value.length + 1) * 10,
        requiresAcceptance: true,
        isActive: true,
        memberTypes: MEMBER_TYPES.map((memberType) => memberType.value),
      },
    ]);
  };

  const toggleCollapsed = (index: number) => {
    setCollapsedItems((current) => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const toggleMemberType = (
    index: number,
    memberType: RegistrationMemberType,
  ) => {
    const template = value[index];
    const memberTypes = template.memberTypes.includes(memberType)
      ? template.memberTypes.filter((item) => item !== memberType)
      : [...template.memberTypes, memberType];
    update(index, { memberTypes });
  };

  return (
    <section className="service-agreement-editor">
      <div className="service-agreement-editor-heading">
        <div>
          <h3>Agreements and consents</h3>
          <p>
            Add the wording shown during registration and choose which member
            types it applies to.
          </p>
        </div>
        <div className="service-agreement-editor-actions">
          <button
            type="button"
            className="link-button link-button-with-icon"
            onClick={() => setCollapsedItems(new Set())}
            disabled={value.length === 0 || collapsedItems.size === 0}
          >
            <UnfoldMoreIcon fontSize="small" />
            Expand all
          </button>
          <button
            type="button"
            className="link-button link-button-with-icon"
            onClick={() =>
              setCollapsedItems(new Set(value.map((_, index) => index)))
            }
            disabled={value.length === 0 || collapsedItems.size === value.length}
          >
            <UnfoldLessIcon fontSize="small" />
            Collapse all
          </button>
          <button
            type="button"
            className="action-button compact secondary"
            onClick={addTemplate}
          >
            <AddIcon fontSize="small" />
            Add item
          </button>
        </div>
      </div>

      {value.length === 0 ? (
        <div className="service-agreement-editor-empty">
          No agreements or consents have been configured for this service.
        </div>
      ) : (
        <div className="service-agreement-template-list">
          {value.map((template, index) => (
            <article
              key={`${template.serviceAgreementTemplateId || "new"}-${index}`}
              className={`service-agreement-template${
                collapsedItems.has(index)
                  ? " service-agreement-template--collapsed"
                  : ""
              }`}
            >
              <div className="service-agreement-template-topline">
                {collapsedItems.has(index) ? (
                  <button
                    type="button"
                    className="service-agreement-collapsed-summary"
                    onClick={() => toggleCollapsed(index)}
                    aria-label={`Expand ${template.name || "item"}`}
                    aria-expanded={false}
                  >
                    <span
                      className={`service-agreement-type-chip service-agreement-type-chip--${template.type.toLowerCase()}`}
                    >
                      {template.type}
                    </span>
                    <strong title={template.name}>
                      {template.name || `Untitled ${template.type.toLowerCase()}`}
                    </strong>
                  </button>
                ) : (
                  <>
                    <div className="form-group">
                      <label className="form-label">Name</label>
                      <input
                        className="form-input"
                        value={template.name}
                        placeholder={
                          template.type === "Consent"
                            ? "e.g. Photography consent"
                            : "e.g. Volunteer code of conduct"
                        }
                        onChange={(event) =>
                          update(index, { name: event.target.value })
                        }
                      />
                    </div>
                    <div className="form-group service-agreement-type-field">
                      <label className="form-label">Type</label>
                      <select
                        className="form-input"
                        value={template.type}
                        onChange={(event) =>
                          update(index, {
                            type: event.target.value as "Agreement" | "Consent",
                          })
                        }
                      >
                        <option value="Agreement">Agreement</option>
                        <option value="Consent">Consent</option>
                      </select>
                    </div>
                  </>
                )}
                <div className="service-agreement-template-actions">
                  <button
                    type="button"
                    className="icon-button service-agreement-remove"
                    aria-label={`Remove ${template.name || "item"}`}
                    onClick={() => {
                      onChange(
                        value.filter((_, itemIndex) => itemIndex !== index),
                      );
                      setCollapsedItems(new Set());
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </button>
                  <button
                    type="button"
                    className="icon-button service-agreement-collapse"
                    aria-label={`${collapsedItems.has(index) ? "Expand" : "Collapse"} ${template.name || "item"}`}
                    aria-expanded={!collapsedItems.has(index)}
                    onClick={() => toggleCollapsed(index)}
                  >
                    {collapsedItems.has(index) ? (
                      <ExpandMoreIcon fontSize="small" />
                    ) : (
                      <ExpandLessIcon fontSize="small" />
                    )}
                  </button>
                </div>
              </div>

              {!collapsedItems.has(index) && (
              <><div className="form-group">
                <label className="form-label">Wording</label>
                <textarea
                  className="form-input service-agreement-text"
                  value={template.text}
                  placeholder="Enter the agreement or consent wording shown to the applicant."
                  onChange={(event) =>
                    update(index, { text: event.target.value })
                  }
                />
              </div>

              <div className="service-agreement-applicability">
                <div>
                  <strong>Show for</strong>
                  <span>Select one or more member types.</span>
                </div>
                <div className="service-agreement-member-types">
                  {MEMBER_TYPES.map((memberType) => (
                    <label key={memberType.value} className="option-item">
                      <input
                        type="checkbox"
                        checked={template.memberTypes.includes(memberType.value)}
                        onChange={() =>
                          toggleMemberType(index, memberType.value)
                        }
                      />
                      {memberType.label}
                    </label>
                  ))}
                </div>
              </div>

              <label className="option-item service-agreement-required">
                <input
                  type="checkbox"
                  checked={template.requiresAcceptance}
                  onChange={(event) =>
                    update(index, { requiresAcceptance: event.target.checked })
                  }
                />
                {template.type === "Consent"
                  ? "A response is required"
                  : "Acceptance is required"}
              </label>
              </>
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
